import { IconDashboard, IconLogout, IconUsers, IconMail, IconTable } from "@tabler/icons-react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { cn } from "../lib/utils";

const navigation = [
	{ name: "Dashboard", href: "/admin", icon: IconDashboard },
	{ name: "Visitors", href: "/admin/visitors", icon: IconUsers },
	{ name: "Contact Forms", href: "/admin/contact-forms", icon: IconMail },
	{ name: "Customer Data", href: "/admin/customer-data", icon: IconTable },
];

export default function AdminLayout({ children }: { children: React.ReactNode }) {
	const { logout } = useAuth();
	const location = useLocation();
	const navigate = useNavigate();

	const isActivePath = (path: string) => {
		if (path === "/admin") {
			return location.pathname === path;
		}
		return location.pathname.startsWith(path);
	};

	const handleLogout = async () => {
		try {
			await logout();
			navigate("/admin/login");
		} catch (error) {
			console.error("Failed to logout:", error);
		}
	};

	return (
		<div className='min-h-screen bg-gray-50 dark:bg-gray-900'>
			<div className='flex min-h-screen'>
				{/* Sidebar */}
				<div className='hidden md:flex md:w-72 md:flex-col'>
					<div className='flex flex-grow flex-col overflow-y-auto border-r border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 px-4'>
						<div className='flex h-16 flex-shrink-0 items-center'>
							<img className='mx-auto w-48 scale-[1.5] mt-2' src={require("../assets/logo.png")} alt='Logo' />
						</div>
						<div className='mt-5 flex flex-1 flex-col'>
							<nav className='flex-1 space-y-1 z-20'>
								{navigation.map((item) => {
									const Icon = item.icon;
									const isActive = isActivePath(item.href);
									return (
										<Link
											key={item.name}
											to={item.href}
											className={cn(
												isActive
													? "bg-gray-100 dark:bg-gray-800 text-primary"
													: "text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800 hover:text-gray-900 dark:hover:text-gray-200",
												"group flex items-center rounded-lg px-4 py-2.5 text-sm font-medium transition-all"
											)}>
											<Icon
												className={cn(
													isActive
														? "text-primary"
														: "text-gray-400 dark:text-gray-500 group-hover:text-gray-500 dark:group-hover:text-gray-400",
													"mr-3 h-5 w-5 flex-shrink-0 transition-colors"
												)}
											/>
											{item.name}
										</Link>
									);
								})}
							</nav>
						</div>
						<div className='mb-4 flex flex-shrink-0 border-t border-gray-200 dark:border-gray-800 pt-4'>
							<button
								onClick={handleLogout}
								className='group flex w-full items-center rounded-lg px-4 py-2.5 text-sm font-medium text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800 hover:text-gray-900 dark:hover:text-gray-200 transition-all'>
								<IconLogout className='mr-3 h-5 w-5 flex-shrink-0 text-gray-400 dark:text-gray-500 group-hover:text-gray-500 dark:group-hover:text-gray-400 transition-colors' />
								Logout
							</button>
						</div>
					</div>
				</div>

				{/* Main content */}
				<div className='flex flex-1 flex-col overflow-hidden bg-gray-50 dark:bg-gray-900'>
					<main className='flex-1 overflow-y-auto px-6 py-8'>
						<div className='mx-auto max-w-7xl'>{children}</div>
					</main>
				</div>
			</div>
		</div>
	);
}
